/* istanbul ignore file */
import {
  Box,
  Button,
  Dialog,
  Flex,
  Heading,
  Loader,
  Text,
} from '@kandji-inc/nectar-ui';
import type React from 'react';
import { useApolloMeetings } from 'src/config/apollo-meetings';
import { useSidebarOverlay } from 'src/hooks/useSidebarOverlay';
import { i18n } from 'src/i18n';
import SalesInterventionSuccessImage from './assets/sales-intervention-success.png';
import SalesInterventionImage from './assets/sales-intervention.png';

interface MeetingDialogProps {
  showMeetingModal: boolean;
  showSuccessModal: boolean;
  meetingBooked: boolean;
  onMeetingModalChange: (isOpen: boolean) => void;
  onSuccessModalChange: (isOpen: boolean) => void;
  onBookDemo: () => void;
}

export const MeetingDialog: React.FC<MeetingDialogProps> = ({
  showMeetingModal,
  showSuccessModal,
  meetingBooked,
  onMeetingModalChange,
  onSuccessModalChange,
  onBookDemo,
}) => {
  const { isReady } = useApolloMeetings();

  useSidebarOverlay(showMeetingModal, 'hubspot-meeting');
  useSidebarOverlay(showSuccessModal, 'hubspot-meeting-success');

  return (
    <>
      <Dialog
        isOpen={showMeetingModal}
        onOpenChange={onMeetingModalChange}
        closeOnEscape={true}
        closeOnOutsideClick={true}
        closeIcon={null}
        content={
          <Box css={{ width: 480 }}>
            <Flex
              flow="column"
              alignItems="center"
              justifyContent="left"
              css={{ width: '100%' }}
            >
              <Box
                css={{
                  height: '200px',
                  width: '100%',
                  backgroundImage: `url(${SalesInterventionImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <Flex
                flow="column"
                css={{ width: '100%', pt: '32px', pb: '32px' }}
              >
                <Heading size="3" css={{ textAlign: 'left', width: '100%' }}>
                  {i18n.t('Ready to see Kandji in action?')}
                </Heading>
                <Text size="2">
                  {i18n.t(
                    'Teams typically benefit from a live walkthrough. Our experts will tailor the demo to your needs.',
                  )}
                </Text>
              </Flex>
              <Button
                variant="primary"
                size="lg"
                css={{
                  width: '100%',
                  position: 'relative',
                  '&:disabled': {
                    opacity: 0.7,
                    cursor: 'not-allowed',
                  },
                }}
                onClick={onBookDemo}
                disabled={!isReady}
                data-testid="interruptive-book-demo-button"
              >
                {!isReady ? (
                  <Flex gap="xs" alignItems="center">
                    <Loader size="sm" />
                    {i18n.t('Loading...')}
                  </Flex>
                ) : (
                  i18n.t('Book your demo now')
                )}
              </Button>
            </Flex>
          </Box>
        }
      />
      <Dialog
        isOpen={meetingBooked && showSuccessModal}
        onOpenChange={onSuccessModalChange}
        closeOnEscape={true}
        closeOnOutsideClick={true}
        closeIcon={null}
        content={
          <Box css={{ width: 480 }}>
            <Box
              css={{
                height: '200px',
                width: '100%',
                backgroundImage: `url(${SalesInterventionSuccessImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <Flex
              flow="column"
              alignItems="center"
              justifyContent="left"
              css={{ width: '100%' }}
            >
              <Flex
                flow="column"
                css={{ width: '100%', pt: '32px', pb: '32px' }}
              >
                <Heading size="3" css={{ textAlign: 'left', width: '100%' }}>
                  {i18n.t("Let's keep the momentum going")}
                </Heading>
                <Text size="2">
                  {i18n.t(
                    "With your demo booked, now's the perfect time to explore even more of what Kandji can do. You're already on your way — dig in, try things out, and get closer to mastering the platform.",
                  )}
                </Text>
              </Flex>
              <Button
                variant="primary"
                size="lg"
                css={{
                  width: '100%',
                }}
                onClick={() => onSuccessModalChange(false)}
                data-testid="continue-my-trial-button"
              >
                {i18n.t('Continue my trial')}
              </Button>
            </Flex>
          </Box>
        }
      />
    </>
  );
};
